// deps
import React, { memo, useCallback, useRef } from "react";

// internal-deps
import { ToingUIWrapper } from "@cart-client/components/common-ui-wrapper";
import CartHeaderContentContainer from "@cart-client/components/toing-cart-renderer/cart-header-content/CartHeaderContentContainer";
import {
    GlobalHeaderState,
    SwgyHeaderKeysList,
    closeWebview,
    useCustomHardwareBack,
    CheckoutRoute,
} from "@swiggy-private/checkout-core";
import { FreeCashRemovalPopupVariant } from "@swiggy-private/cart-types";
import { useNativeCustomBackHandling } from "@swiggy-private/payment-client";
import { useFreeCashRemovalPopup } from "@cart-client/contexts/FreeCashRemovalPopupContext";

// types
import { CartUIWrapperProps } from "./types";

export interface ScrollableDivElement extends HTMLDivElement {
    getScrollPosition: () => number;
}

function ToingCartUIWrapper({ children, ...props }: CartUIWrapperProps): React.ReactElement {
    const { containerId, onBack, isFreeCashApplicable = false } = props;

    const { popupConfig, showPopup, hidePopup } = useFreeCashRemovalPopup();

    const isFromBottomBar = GlobalHeaderState.getHeader(SwgyHeaderKeysList.isFromBottomBar) === "true";

    const _onBack = useCallback(() => {
        if (isFromBottomBar) {
            closeWebview({
                params: {
                    goToHome: "true",
                },
            });
            return;
        }
        closeWebview();
    }, [isFromBottomBar]);

    // Back handler: use prop override when provided; fall back to internal logic
    const backHandler = useCallback(() => {
        if (typeof onBack === "function") {
            onBack();
            return;
        }
        _onBack();
    }, [onBack, _onBack]);

    const onBackWithFreeCashCheck = useCallback(() => {
        if (isFreeCashApplicable) {
            showPopup({
                variant: FreeCashRemovalPopupVariant.MENU_NAVIGATION,
                onPrimaryClick: () => {
                    hidePopup();
                    backHandler();
                },
                onSecondaryClick: hidePopup,
                analyticsParams: {
                    warningTrigger: "back_press",
                },
            });
            return;
        }
        backHandler();
    }, [isFreeCashApplicable, showPopup, hidePopup, backHandler]);

    useCustomHardwareBack([CheckoutRoute.TOING_CART], onBackWithFreeCashCheck);

    const nativeBackHandler = useCallback(() => {
        if (popupConfig) {
            hidePopup();
            return;
        }
        onBackWithFreeCashCheck();
    }, [popupConfig, hidePopup, onBackWithFreeCashCheck]);

    useNativeCustomBackHandling(nativeBackHandler);

    const containerRef = useRef<ScrollableDivElement>(null);

    return (
        <ToingUIWrapper
            restaurantName={props.restaurantName}
            onBack={onBackWithFreeCashCheck}
            header={<CartHeaderContentContainer {...props} />}
            content={children}
            key={0}
            ref={containerRef}
            containerId={containerId}
            isCartError={props.isCartError}
            isCartLoading={props.isCartLoading}
            isEmptyCart={props.isEmptyCart}
            isPageScrolled={props.isPageScrolled}
            showAddressWidgetTop={props.showAddressWidgetTop}
            openHalfCard={props.openHalfCard}
            isFreeCashApplicable={isFreeCashApplicable}
        />
    );
}

export default memo(ToingCartUIWrapper);
